import { cn } from '@listening/utils/react-helpers';
import { Schema } from 'effect';
import { createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight } from 'iconoir-react';
import { blogIndexSearchSchema } from '@/analytics/cacheable-route-search';
import { CardBig } from '@/components/astro/blog/cards/card-big';
import { CardMedium } from '@/components/astro/blog/cards/card-medium';
import { CardSmall } from '@/components/astro/blog/cards/card-small';
import { CtaBanner } from '@/components/astro/blog/cta-banner';
import { Footer } from '@/components/astro/new-design-25/ui/Footer';
import { NavBar } from '@/components/astro/new-design-25/ui/NavBar';

import { normalizeCanonicalUrl } from '@/seo/canonical';
import { seo } from '@/seo/seo';
import { callApiPromise } from '@/runtimes/get-runtime';
import { cacheHeadersFor, experimentalRoutes } from '@/analytics';

const blogPaginationLinkClassName = 'p-1 hover:underline rounded';

// stale-if-error keeps a working page in front of users when WordPress upstream
// is timing out, so a WP Engine origin blip doesn't surface as a 500.
const BLOG_INDEX_CACHE_CONTROL =
  'public, max-age=0, s-maxage=3600, stale-while-revalidate=86400, stale-if-error=86400';

const blogIndexRouteAnalytics = experimentalRoutes['/_marketing/blog/'];

export const Route = createFileRoute('/_marketing/blog/')({
  // Declared in the leaf cacheable-route-search module: `page` is a server
  // render input on a cached route, so it must be in the CDN cache key.
  validateSearch: Schema.toStandardSchemaV1(blogIndexSearchSchema),
  staticData: { analytics: blogIndexRouteAnalytics },
  headers: cacheHeadersFor(blogIndexRouteAnalytics, BLOG_INDEX_CACHE_CONTROL),
  loaderDeps: ({ search: { page } }) => ({ page }),
  loader: ({ deps, abortController }) =>
    callApiPromise(
      (api) =>
        api.Blog.ListPosts({
          query: { page: deps.page ?? 1, size: 15 },
        }),
      { signal: abortController.signal },
    ).then(({ posts: rawPosts, pageInfo }) => {
      const posts = rawPosts.map(({ slug, ...rest }) => ({
        slug,
        data: rest,
      }));
      const canonicalPath =
        deps.page && deps.page > 1
          ? `/blog?page=${deps.page.toString()}`
          : '/blog';
      return {
        posts,
        pageInfo,
        canonical: normalizeCanonicalUrl(canonicalPath),
      };
    }),
  component: Page,
  head: ({ loaderData }) => ({
    meta: [
      ...seo({
        title: "Listening's Latest Blogs and News",
        description:
          'Stay informed with the latest news and updates from our platform. Read comprehensive articles and gain insights on key trends. Start exploring now!',
      }),
    ],
    links: loaderData?.canonical
      ? [{ rel: 'canonical', href: loaderData.canonical }]
      : undefined,
  }),
});

function Page() {
  const { posts, pageInfo } = Route.useLoaderData();
  const { page = 1 } = Route.useSearch();

  const pageNumberLinks = (() => {
    const delta = 3;
    const maxPage = pageInfo.totalPages;
    const startPos = Math.max(page - delta, 2);
    const endPos = Math.min(
      page + delta + (delta - (page - startPos)),
      maxPage - 1,
    );
    const links: Array<{ pageNum: number; label: string }> = [];

    if (maxPage > 1) {
      links.push({ pageNum: 1, label: '1' });
      if (startPos > 2) {
        links.push({ pageNum: startPos, label: '...' });
      }
      for (
        let i = Math.max(startPos, 2);
        i <= Math.min(endPos, maxPage - 1);
        i++
      ) {
        links.push({ pageNum: i, label: i.toString() });
      }
      if (endPos < maxPage - 1) {
        links.push({ pageNum: endPos, label: '...' });
      }
      if (maxPage > 1) {
        links.push({ pageNum: maxPage, label: maxPage.toString() });
      }
    }
    return links;
  })();
  return (
    <>
      <NavBar logoLinkHref={page > 1 ? '/blog' : undefined} sticky />
      <h1 aria-hidden='false' className='hidden'>
        Blog
      </h1>
      <main className='m-auto flex w-[1296px] max-w-[calc(100%-2em)] flex-col gap-14 overflow-hidden py-14 md:gap-18 md:py-18'>
        {page === 1 && (
          <>
            <header className='grid grid-cols-1 gap-14 md:grid-cols-2 md:gap-6 xl:grid-cols-3'>
              <div className='xl:col-span-2'>
                {
                  // LAST ONE WAS PUBLISHED
                  posts[0] && (
                    <CardBig slug={posts[0].slug} {...posts[0].data} />
                  )
                }
              </div>
              <div className='flex flex-col gap-4 md:gap-[30px]'>
                {
                  // SHOW LAST 2,3,4,5 LAST PUBLISHED
                  posts.slice(1, 5).map((post) => (
                    <CardSmall
                      key={post.slug}
                      slug={post.slug}
                      {...post.data}
                    />
                  ))
                }
              </div>
            </header>

            {posts.length > 10 && (
              <>
                <hr className='border-neutral-210' />
                <section className='grid grid-cols-1 gap-14 md:grid-cols-2 md:gap-6 xl:grid-cols-3'>
                  {posts.slice(5, 11).map((post) => (
                    // SHOW LAST 6,7,8,9,10,11 LAST PUBLISHED
                    <CardSmall
                      key={post.slug}
                      slug={post.slug}
                      {...post.data}
                    />
                  ))}
                </section>
              </>
            )}
            <hr className='border-neutral-210' />
            <CtaBanner />
            {posts.length > 14 && (
              <>
                <hr className='border-neutral-210' />

                <section className='flex gap-4 px-4 pb-4 xl:gap-6'>
                  {posts.slice(11, 15).map((post) => (
                    // SHOW LAST 12,13,14,15 LAST PUBLISHED
                    <CardMedium
                      author={post.data.author}
                      authorImage={post.data.authorImage}
                      description={post.data.description}
                      href={normalizeCanonicalUrl(`/blog/${post.slug}`)}
                      heroImage={post.data.heroImage}
                      heroImageAlt={post.data.heroImageAlt}
                      key={post.slug}
                      pubDate={post.data.pubDate}
                      tags={post.data.tags.slice(0, 2)}
                      title={post.data.title}
                    />
                  ))}
                </section>
              </>
            )}
          </>
        )}
        {page > 1 && (
          <section className='flex flex-wrap justify-center gap-4 px-4 pb-4 xl:gap-6'>
            {posts.map((post) => (
              // SHOW LAST 12,13,14,15 LAST PUBLISHED
              <CardMedium
                author={post.data.author}
                authorImage={post.data.authorImage}
                description={post.data.description}
                href={normalizeCanonicalUrl(`/blog/${post.slug}`)}
                heroImage={post.data.heroImage}
                heroImageAlt={post.data.heroImageAlt}
                key={post.slug}
                pubDate={post.data.pubDate}
                tags={post.data.tags.slice(0, 2)}
                title={post.data.title}
              />
            ))}
          </section>
        )}
        <div className='flex justify-between px-4'>
          {pageInfo.hasPreviousPage ? (
            <a
              href={normalizeCanonicalUrl(
                `/blog?page=${pageInfo.previousPage?.toString() ?? (page - 1).toString()}`,
              )}
              className={blogPaginationLinkClassName}
            >
              <ArrowLeft className='size-6' />
            </a>
          ) : (
            <div />
          )}
          <div className='flex items-center gap-3'>
            {pageNumberLinks.map((link) => (
              <a
                key={link.pageNum}
                href={normalizeCanonicalUrl(
                  `/blog?page=${link.pageNum.toString()}`,
                )}
                className={cn(
                  blogPaginationLinkClassName,
                  page === link.pageNum ? 'bg-primary text-white' : '',
                )}
              >
                {link.label}
              </a>
            ))}
          </div>
          {pageInfo.hasNextPage ? (
            <a
              href={normalizeCanonicalUrl(
                `/blog?page=${pageInfo.nextPage?.toString() ?? (page + 1).toString()}`,
              )}
              className={blogPaginationLinkClassName}
            >
              <ArrowRight className='size-6' />
            </a>
          ) : (
            <div />
          )}
        </div>
      </main>
      <Footer
      // className="mt-[65px]"
      />
    </>
  );
}
